import { FieldError } from 'react-hook-form';

import { NavigableItem } from 'services/schemableFormNavigation/schemableFormNavigation';
import { PartyIdentifierDTO } from 'support/types';
import portalSchema from 'support/types/schema-portal';

import { SECTION_TYPES } from './annotationItems/sectionTypes';

export type UnmatchedArticleAnnotationValue = portalSchema.components['schemas']['UnmatchedArticleAnnotationValue'];

export type { ConfigItem } from 'services/schemableFormNavigation/types';

export type SectionTypeKey = keyof typeof SECTION_TYPES;
export type SectionTypeValue = (typeof SECTION_TYPES)[SectionTypeKey];

type WebEDIAnnotationBase = NavigableItem & {
  annotationId?: string;
};

export type WebEDIError = WebEDIAnnotationBase & {
  severity: 'error';
  isFormError: true;
  type: FieldError['type'];
  title: string;
  description: string;
};

export enum WebEDIAnnotationCode {
  UNMATCHED_ARTICLE = 'UNMATCHED_ARTICLE',
  EPHEMERAL_PARTNER = 'EPHEMERAL_PARTNER',
  CONNECTOR_HOOK = 'CONNECTOR_HOOK',
  OCR_VERIFICATION_WARNING = 'VERIFICATION_WARNING',
}

export type WebEDIUnmatchedArticleWarning = WebEDIAnnotationBase & {
  severity: 'warning';
  code: WebEDIAnnotationCode.UNMATCHED_ARTICLE;
  currentValue: UnmatchedArticleAnnotationValue;
};

export type WebEDIEphemeralPartnerWarning = WebEDIAnnotationBase & {
  severity: 'warning';
  code: WebEDIAnnotationCode.EPHEMERAL_PARTNER;
  currentValue: {
    name: string;
    identifiers: Array<PartyIdentifierDTO>;
  };
};

export type WebEDIConnectorHookWarning = WebEDIAnnotationBase & {
  severity: 'warning';
  code: WebEDIAnnotationCode.CONNECTOR_HOOK;
  currentValue: {
    translatedMessage: string;
  };
};

export type WebEDIConnectorHookError = WebEDIAnnotationBase & {
  severity: 'error';
  code: WebEDIAnnotationCode.CONNECTOR_HOOK;
  currentValue: {
    translatedMessage: string;
  };
};

export type WebEDIOcrVerificationWarning = WebEDIAnnotationBase & {
  severity: 'warning';
  code: WebEDIAnnotationCode.OCR_VERIFICATION_WARNING;
  currentValue: {
    translatedMessage: string;
  };
};

export type WebEDIWarning =
  | WebEDIUnmatchedArticleWarning
  | WebEDIEphemeralPartnerWarning
  | WebEDIConnectorHookWarning
  | WebEDIOcrVerificationWarning;

export type WebEDIAnnotation = WebEDIError | WebEDIWarning | WebEDIConnectorHookError;

export const isWebEDIFormError = (annotation: WebEDIAnnotation | undefined): annotation is WebEDIError => {
  return annotation?.isFormError === true;
};
